import React, { useState, useEffect, useRef } from 'react';

interface TextBubbleProps {
    id: string | number;
    text: string;
    width: number;
    height: number;
    style?: 'none' | 'speech' | 'thought' | 'shout' | 'rectangle';
    fontFamily?: string;
    color?: string;
    bubbleColor?: string;
    align?: 'left' | 'center' | 'right';
    onUpdate: (id: string | number, updates: any) => void;
    isSelected: boolean;
}

export const TextBubble: React.FC<TextBubbleProps> = ({
    id,
    text,
    width,
    height,
    style = 'speech',
    fontFamily = 'Inter, sans-serif',
    color = '#0C0C0C',
    bubbleColor = '#ffffff',
    align = 'center',
    onUpdate,
    isSelected
}) => {
    // Local text state for smooth typing
    const [localText, setLocalText] = useState(text);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        setLocalText(text);
    }, [text]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setLocalText(e.target.value);
        onUpdate(id, { text_content: e.target.value });

        // Auto-grow bubble if text overflows?
        // For now, let's keep it simple: text flows, scroll if needed, 
        // but user asked for "unless resizing text makes it bigger than bubble".
        // That implies manual font size control or auto-fit.
    };

    // Calculate font size relative to bubble size for "resizing bubble resizes text"
    // Heuristic: Base font size is proportional to height or width.
    const fontSize = Math.min(width, height) * 0.15;

    // Bubble Shapes (Basic SVGs)
    const renderBubbleBg = () => {
        const commonStyle: React.CSSProperties = {
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            fill: bubbleColor,
            stroke: '#000',
            strokeWidth: 3,
            pointerEvents: 'none' // Click through to text/container
        };

        switch (style) {
            case 'speech':
                return (
                    <svg viewBox="0 0 200 150" preserveAspectRatio="none" style={commonStyle}>
                        <path d="M10,10 H190 Q200,10 200,20 V110 Q200,120 190,120 H60 L30,145 L40,120 H10 Q0,120 0,110 V20 Q0,10 10,10 Z" />
                    </svg>
                );
            case 'thought':
                return (
                    <svg viewBox="0 0 200 150" preserveAspectRatio="none" style={commonStyle}>
                        <path d="M40,20 Q60,0 80,20 Q100,0 120,20 Q140,0 160,20 Q180,0 190,30 Q200,50 190,70 Q200,90 190,110 Q180,130 160,120 Q140,140 120,120 Q100,140 80,120 Q60,140 40,120 Q20,130 10,110 Q0,90 10,70 Q0,50 10,30 Q20,0 40,20 Z M50,130 A5,5 0 0,1 55,135 A5,5 0 0,1 50,140 A5,5 0 0,1 45,135 A5,5 0 0,1 50,130 Z M30,145 A3,3 0 0,1 33,148 A3,3 0 0,1 30,151 A3,3 0 0,1 27,148 A3,3 0 0,1 30,145 Z" />
                    </svg>
                );
            case 'shout':
                return (
                    <svg viewBox="0 0 200 150" preserveAspectRatio="none" style={commonStyle}>
                        <path d="M20,10 L50,20 L80,5 L110,20 L150,5 L160,30 L195,40 L170,70 L195,100 L160,110 L150,145 L110,120 L80,145 L50,120 L10,130 L30,90 L5,60 L30,30 Z" />
                    </svg>
                );
            case 'rectangle':
                return (
                    <svg viewBox="0 0 200 150" preserveAspectRatio="none" style={commonStyle}>
                        <rect x="2" y="2" width="196" height="146" strokeWidth="4" />
                    </svg>
                );
            default: // None (Transparent text only)
                return null;
        }
    };

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {renderBubbleBg()}

            <textarea
                ref={textAreaRef}
                value={localText}
                onChange={handleChange}
                placeholder="Type here..."
                style={{
                    position: 'relative',
                    zIndex: 10,
                    width: '80%',
                    height: '70%',
                    background: 'transparent',
                    border: 'none',
                    resize: 'none',
                    outline: 'none',
                    fontFamily: fontFamily,
                    fontSize: `${fontSize}px`,
                    color: color,
                    textAlign: align,
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: align
                }}
                className="placeholder-black/30 w-full h-full bg-transparent"
                onPointerDown={(e) => {
                    // Allow clicking text area to focus, but don't drag if focused?
                    // Actually CanvasItem's onPointerDown handles drag. 
                    // We need to stopPropagation if we want to TYPE.
                    e.stopPropagation();
                }}
            />
        </div>
    );
};
